import React from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import * as S from './TabBar.styles';
import { useScrollVisibility } from '@/hooks/useScrollVisibility';
import { tabBarLinks } from '@/components/TabBar/TabBar.constants';
import { useHasUnreadMessages } from '@/utils/pubnub/useHasUnreadMessages';
import { isCurrentPage } from '@/components/TabBar/TabBar.helpers';
import { usePageTopControlsStore } from '@/store/usePageTopControlsStore';
import {
  clearLastAdsData,
  clearLastScrollPositionData,
} from '@/components/ScrollSaver/ScrollSaver.helpers';
import { ROUTES } from '@/constants/routes';
import { useSaveScrollPositionActions } from '@/components/ScrollSaver/ScrollSaver.hooks';
import { useAuthStore } from '@/store/useAuthStore';
import { useBridgeState } from '@/bridge/hooks';
import { getNewAdPageHref } from '@/utils/helpers';
import { useCustomPush } from '@/components/CustomLink/CustomLink.hooks';
import { Button } from '@/components/UI/Button/Button';
import { TabBarIcon, TabBarIconName } from '@/components/UI/Icons/TabBarIcon';

export const TabBar = () => {
  const { t } = useTranslation();
  const { asPath } = useRouter();
  const hasUnreadMessages = useHasUnreadMessages();
  const [isAuthLoaded, isAuthenticated] = useAuthStore(state => [
    state.isAuthLoaded,
    state.isAuthenticated,
  ]);
  const saveScrollPositionActions = useSaveScrollPositionActions();
  const { isVisible, isEndOfPage, scrollPos } = useScrollVisibility();
  const topControlsHeight = usePageTopControlsStore(
    state => state.topControlsHeight
  );
  const { isWebView } = useBridgeState();
  const $isVisible =
    isWebView || scrollPos <= topControlsHeight || isVisible || isEndOfPage;
  const customPush = useCustomPush();

  function saveScrollPosition(href: string) {
    if (href === ROUTES.home) {
      clearLastScrollPositionData();
      clearLastAdsData();
      return;
    }

    saveScrollPositionActions();
  }

  function handleClick(
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    href: string
  ) {
    if (href === ROUTES.home) {
      e.preventDefault();
      window.location.href = href;
    } else {
      saveScrollPosition(href);
    }
  }

  return (
    <>
      <S.TabBarBoxForSize />
      <S.TabBarBox $isVisible={$isVisible} className="tab-bar-container">
        {tabBarLinks(t, isAuthLoaded, isAuthenticated, asPath).map(
          ({ href, iconName, isBig, text, isDisabled }) => {
            const disabled = isDisabled || !isAuthLoaded;
            return (
              <S.TabBarLink
                $isDisabled={disabled}
                aria-disabled={disabled}
                tabIndex={disabled ? -1 : undefined}
                key={iconName}
                href={href}
                onClick={e => handleClick(e, href)}
                {...(href === getNewAdPageHref(asPath) && {
                  as: Button,
                  href: undefined,
                  appearance: 'transparent',
                  hasPadding: false,
                  onClick: () => {
                    saveScrollPosition(href);
                    customPush(href);
                  },
                })}
                className="tab-bar__link"
              >
                <S.TabBarLinkIconContainer $size={isBig ? 45 : 20}>
                  <TabBarIcon
                    name={iconName as TabBarIconName}
                    isActive={isCurrentPage(href, asPath)}
                  />
                  {href === '/messages' && hasUnreadMessages && (
                    <S.TabBarUnreadIcon />
                  )}
                </S.TabBarLinkIconContainer>
                <S.TabBarLinkText $isActive={isCurrentPage(href, asPath)}>
                  {text}
                </S.TabBarLinkText>
              </S.TabBarLink>
            );
          }
        )}
      </S.TabBarBox>
    </>
  );
};
